import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useOnboardingState } from '../../hooks/useOnboardingState.jsx';
import { onboardingV2Api } from '@/data/api/onboardingV2';
import Logo from '@/Asset/images/logo-blaxk.webp';
import TurnsImage from '@/Asset/images/turn.webp';
import JumpsImage from '@/Asset/images/jump.webp';
import FlexibilityImage from '@/Asset/images/flexibility.webp';
import StrengthImage from '@/Asset/images/strength.webp';
import RehabImage from '@/Asset/images/injury.webp';
import EducatorVisual from '@/Asset/images/step-3.webp';
import EastIcon from '@mui/icons-material/East';
import Toast from '@/utils/toastManager';
import StepIndicator from '../StepIndicator/StepIndicator';
import './Step3.css';

const Step3 = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { state, updateState } = useOnboardingState();
	const roleFromQuery = useMemo(() => new URLSearchParams(location.search).get('role'), [location.search]);
	const planFromQuery = useMemo(() => new URLSearchParams(location.search).get('plan'), [location.search]);
	const isRetake = useMemo(() => new URLSearchParams(location.search).get('retake') === 'true' || location.state?.isRetake === true, [location.search, location.state?.isRetake]);
	const selectedRole = roleFromQuery || state.onboardingRole || null;
	const isStudioPlan = state.selectedPlanType === 'studio' || planFromQuery === 'studio';
	const isPlusPlan = planFromQuery === 'plus';
	const isEducatorFlow = selectedRole === 'dance-educator' || selectedRole === 'studio-owner';
	const isDancerParentFlow = !isStudioPlan && selectedRole === 'dancer-parent';
	const isStandardAltFlow = !isStudioPlan && !isDancerParentFlow;
	const isPlusEducatorFlow = isPlusPlan && isEducatorFlow;
	const totalSteps = isStudioPlan ? (isRetake ? 5 : 6) : 4;
	const stepItems = Array.from({ length: totalSteps }, (_, index) => index + 1);
	const dancerParentFocusOptions = useMemo(
		() => [
			{ id: 'turns', label: 'Turns', image: TurnsImage },
			{ id: 'jumps', label: 'Jumps', image: JumpsImage },
			{ id: 'flexibility', label: 'Flexibility', image: FlexibilityImage },
			{ id: 'strength', label: 'Strength', image: StrengthImage },
			{ id: 'injury-reduction-rehab', label: 'Injury Reduction / Rehab', image: RehabImage }
		],
		[]
	);
	const studioGoalOptions = useMemo(
		() => [
			{ id: 'strength-development', label: 'Strength Development', image: TurnsImage },
			{ id: 'enhanced-flexibility', label: 'Enhanced Flexibility', image: FlexibilityImage },
			{ id: 'turns-technique', label: 'Turns Technique', image: RehabImage },
			{ id: 'jumps-technique', label: 'Jumps Technique', image: JumpsImage },
			{ id: 'balance-and-control', label: 'Balance And Control', image: TurnsImage },
			{ id: 'injury-reduction', label: 'Injury Reduction', image: FlexibilityImage },
			{ id: 'conditioning-and-stamina', label: 'Conditioning And Stamina', image: RehabImage },
			{ id: 'across-the-floor-movement-quality', label: 'Across The Floor / Movement Quality', image: JumpsImage },
			{ id: 'pre-competition-preparation', label: 'Pre-Competition Preparation', image: RehabImage }
		],
		[]
	);
	const [selectedFocusAreas, setSelectedFocusAreas] = useState([]);
	const [selectedStudioGoals, setSelectedStudioGoals] = useState([]);
	const [educatorInput, setEducatorInput] = useState('');

	useEffect(() => {
		if (state.parentFocusAreas) setSelectedFocusAreas(state.parentFocusAreas);
		if (state.studioTrainingGoals) setSelectedStudioGoals(state.studioTrainingGoals);
		if (state.educatorWishlist) setEducatorInput(state.educatorWishlist);
	}, [state.parentFocusAreas, state.studioTrainingGoals, state.educatorWishlist]);

	// Validation function for StepIndicator - checks if current step is valid
	const validateStep = () => {
		if (isDancerParentFlow && selectedFocusAreas.length === 0) {
			Toast.error('Please select at least one focus area to continue.');
			return false;
		}
		if (isStudioPlan && selectedStudioGoals.length === 0) {
			Toast.error('Please select at least one training goal to continue.');
			return false;
		}
		return true;
	};

	// Handle validation failure for StepIndicator
	const handleValidationFail = () => {
		// Toast is already shown in validateStep
	};

	useEffect(() => {
		if (roleFromQuery && roleFromQuery !== state.onboardingRole) {
			updateState({ onboardingRole: roleFromQuery });
		}
	}, [roleFromQuery, state.onboardingRole, updateState]);

	const toggleFocusArea = id => {
		setSelectedFocusAreas(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]));
	};
	const toggleStudioGoal = id => {
		setSelectedStudioGoals(prev => {
			if (prev.includes(id)) return prev.filter(item => item !== id);
			if (prev.length >= 3) return prev;
			return [...prev, id];
		});
	};

	const handleNext = async () => {
		// Validate required selections based on flow
		if (isDancerParentFlow && selectedFocusAreas.length === 0) {
			Toast.error('Please select at least one focus area to continue.');
			return;
		}

		if (isStudioPlan && selectedStudioGoals.length === 0) {
			Toast.error('Please select at least one training goal to continue.');
			return;
		}

		const onboardingPreferences = {
			...(state.onboardingPreferences || {}),
			parentFocusAreas: selectedFocusAreas,
			studioTrainingGoals: selectedStudioGoals,
			educatorWishlist: educatorInput
		};

		try {
			await onboardingV2Api.updateOnboardingData({
				onboardingStep: 7,
				onboardingPreferences
			});
		} catch (error) {
			Toast.error('Failed to save this step. Please try again.');
			return;
		}

		updateState({
			currentStep: 7,
			parentFocusAreas: selectedFocusAreas,
			studioTrainingGoals: selectedStudioGoals,
			educatorWishlist: educatorInput,
			onboardingPreferences
		});
		
		const params = new URLSearchParams(location.search);
		if (isRetake && !isStudioPlan) {
			navigate(`/onboarding/recommended-videos?${params.toString()}`);
			return;
		}

		navigate(`/onboarding/step-4?${params.toString()}`);
	};

	const handleSkip = async () => {
		try {
			await onboardingV2Api.updateOnboardingData({
				onboardingStep: 7
			});
		} catch (error) {
			Toast.error('Failed to save this step. Please try again.');
			return;
		}

		updateState({ currentStep: 7 });
		navigate('/');
	};

	return (
		<div className="onboarding-step-three">
			<div className="onboarding-step-three__grid-overlay" />
			<div className="onboarding-step-three__content">
				<header className="onboarding-step-three__header">
					<div className="onboarding-step-three__brand-row">
						<button
							type="button"
							className="onboarding-step-three__back-btn"
							onClick={() => navigate(`/onboarding/step-2?${new URLSearchParams(location.search).toString()}`)}
						>
							<KeyboardBackspaceOutlinedIcon />
						</button>
						<img src={Logo} alt="The Godfrey Method On Demand" className="onboarding-step-three__logo" />
					</div>

					<StepIndicator 
						totalSteps={totalSteps} 
						currentStep={3}
						validateStep={validateStep}
						onValidationFail={handleValidationFail}
						basePath="/onboarding"
					/>
				</header>

				<main className="onboarding-step-three__main">
					{isDancerParentFlow ? (
						<>
							<h1 className="onboarding-step-three__title">What Are You Looking To Improve Most?</h1>
							<p className="onboarding-step-three__subtitle">Please select all that apply</p>
							<div className="onboarding-step-three__cards onboarding-step-three__cards--five">
								{dancerParentFocusOptions.map(option => {
									const isSelected = selectedFocusAreas.includes(option.id);
									return (
										<button
											key={option.id}
											type="button"
											className={`onboarding-step-three__card ${isSelected ? 'is-selected' : ''}`}
											onClick={() => toggleFocusArea(option.id)}
										>
											<div className="onboarding-step-three__card-image-wrap">
												<img src={option.image} alt={option.label} className="onboarding-step-three__card-image" />
											</div>
											<div className="onboarding-step-three__card-footer">
												<p>{option.label}</p>
												{isSelected ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
											</div>
										</button>
									);
								})}
							</div>
						</>
					) : isStudioPlan ? (
						<>
							<h1 className="onboarding-step-three__title onboarding-step-three__title--studio">
								What are your main training
								<br />
								goals for your dancers?
							</h1>
							<p className="onboarding-step-three__subtitle">Please select up to 3</p>
							<div className="onboarding-step-three__cards onboarding-step-three__cards--nine">
								{studioGoalOptions.map(option => {
									const isSelected = selectedStudioGoals.includes(option.id);
									const isDisabled = !isSelected && selectedStudioGoals.length >= 3;
									return (
										<button
											key={option.id}
											type="button"
											className={`onboarding-step-three__card is-studio ${isSelected ? 'is-selected' : ''} ${isDisabled ? 'is-disabled' : ''}`}
											onClick={() => toggleStudioGoal(option.id)}
											disabled={isDisabled}
										>
											<p className="onboarding-step-three__studio-card-label">{option.label}</p>
											<span className={`onboarding-step-three__studio-card-dot ${isSelected ? 'is-selected' : ''}`}>
												{isSelected ? <CheckIcon /> : null}
											</span>
										</button>
									);
								})}
							</div>
						</>
					) : isStandardAltFlow ? (
						<section className="onboarding-step-three__educator-layout">
							<div className="onboarding-step-three__educator-main">
								<h1 className="onboarding-step-three__title">What Would You Like To See On Godfrey Method On Demand?</h1>
								<div className="onboarding-step-three__input-wrap">
									<textarea
										className="onboarding-step-three__input"
										placeholder="Optional"
										value={educatorInput}
										onChange={event => setEducatorInput(event.target.value)}
									/>
								</div>
								<div className="onboarding-step-three__results-row">
									<button 
										type="button" 
										onClick={handleNext} 
										className="onboarding-step-three__results-btn"
									>
										See My Results <EastIcon />
									</button>
								</div>
							</div>
						</section>
					) : (
						<div className="onboarding-step-three__placeholder">
							<h1 className="onboarding-step-three__title">Step 3</h1>
						</div>
					)}
				</main>

				<footer className="onboarding-step-three__footer">
					{!isStandardAltFlow ? (
						<button 
							type="button" 
							onClick={handleNext} 
							className={`onboarding-step-three__continue-btn ${(isDancerParentFlow && selectedFocusAreas.length === 0) || (isStudioPlan && selectedStudioGoals.length === 0) ? 'is-disabled' : ''}`}
							disabled={(isDancerParentFlow && selectedFocusAreas.length === 0) || (isStudioPlan && selectedStudioGoals.length === 0)}
						>
							Continue <EastIcon />
						</button>
					) : null}
					<button type="button" onClick={handleSkip} className="onboarding-step-three__skip-btn">
						Skip
					</button>
				</footer>
			</div>
		</div>
	);
};

export default Step3;
